/**
 * Plan 147 — General-chat clause attachment.
 *
 * Frontend writer for the bracketed `context_injection` payload used to attach
 * one or more clauses to a free-text chat message. Mirrors the per-block format
 * that ClauseChatThread uses for scoped sessions, with an added `doc="..."`
 * label so multi-clause stacks across documents disambiguate cleanly.
 *
 * No client-side parser: historical chips are populated from a server-parsed
 * `attached_clauses` summary returned on GET /messages, so the raw injection
 * never traverses the wire on read.
 */

export interface AttachedClauseRef {
  clauseId: string;
  clauseTitle: string;
  clauseText: string;
  documentId: string;
  documentName: string;
}

/**
 * Display-only summary returned by the server on GET /messages.
 * Intentionally lacks clauseId/documentId — historical chips are
 * non-interactive (no click-to-navigate), and excluding clause text
 * keeps history payloads small.
 */
export interface AttachedClauseSummary {
  clauseTitle: string;
  documentName: string;
}

/** Plan 171: exported so other context_injection writers (ClauseChatThread)
 *  apply the same escape semantics. Backslash first, then double-quote. */
export const escapeForContextInjection = (s: string) =>
  s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

const escape = escapeForContextInjection;

export function formatAttachedClauses(refs: AttachedClauseRef[]): string {
  return refs
    .map(
      (r) =>
        `[Clause: "${escape(r.clauseTitle)}" doc="${escape(r.documentName)}"]\n` +
        `[Text: "${escape(r.clauseText)}"]`,
    )
    .join('\n\n');
}

/**
 * Normalise the GET /messages `attached_clauses` payload to the frontend
 * camelCase domain shape. The server parser emits snake_case keys
 * (`clause_title`, `document_name`) while the in-memory optimistic chips
 * (built from `useDeskStore`) use camelCase. ChatMessage reads
 * `c.clauseTitle` — without this normaliser the historical chips render
 * with empty labels after a page reload (issue #706).
 *
 * Idempotent — already-camelCase input passes through, which keeps the
 * mapper safe when called against locally-constructed messages too.
 */
export function normalizeAttachedClauseSummaries(
  raw: unknown,
): AttachedClauseSummary[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw.map((c: any) => ({
    clauseTitle: c?.clauseTitle ?? c?.clause_title ?? '',
    documentName: c?.documentName ?? c?.document_name ?? '',
  }));
}
